import { createApp } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import ButtonGroup from 'primevue/buttongroup';
import Card from 'primevue/card';
import Checkbox from 'primevue/checkbox';
import ConfirmationService from 'primevue/confirmationservice';
import ConfirmDialog from 'primevue/confirmdialog';
import ContextMenu from 'primevue/contextmenu';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import DialogService from 'primevue/dialogservice';
import Divider from 'primevue/divider';
import DynamicDialog from 'primevue/dynamicdialog';
import Fieldset from 'primevue/fieldset';
import FileUpload from 'primevue/fileupload';
import FloatLabel from 'primevue/floatlabel';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Paginator from 'primevue/paginator';
import Panel from 'primevue/panel';
import PanelMenu from 'primevue/panelmenu';
import ProgressSpinner from 'primevue/progressspinner';
import Ripple from 'primevue/ripple';
import Select from 'primevue/select';
import SelectButton from 'primevue/selectbutton';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import StyleClass from 'primevue/styleclass';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Tag from 'primevue/tag';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import ToggleButton from 'primevue/togglebutton';
import ToggleSwitch from 'primevue/toggleswitch';
import Tooltip from 'primevue/tooltip';

import CommentBubble from '@/components/comment/Comment.vue';
import ContentTab from '@/components/contentTabs/ContentTab.vue';
import ContentTabs from '@/components/contentTabs/ContentTabs.vue';
import DataTable from '@/components/datatable/DataTable.vue';
import DetailFormfooter from '@/components/details/detail-formfooter/DetailFormfooter.vue';
import DetailRelations from '@/components/details/detail-relations/DetailRelations.vue';
import DetailToolbar from '@/components/details/detail-toolbar/DetailToolbar.vue';
import FileViewer from '@/components/file-viewer/FileViewer.vue';
import FocusArea from '@/components/FocusArea.vue';
import FormCheckbox from '@/components/forms/FormCheckbox.vue';
import FormDatePicker from '@/components/forms/FormDatePicker.vue';
import FormInputNumber from '@/components/forms/FormInputNumber.vue';
import FormInputText from '@/components/forms/FormInputText.vue';
import FormLazySelect from '@/components/forms/FormLazySelect.vue';
import FormSelect from '@/components/forms/FormSelect.vue';
import FormTextArea from '@/components/forms/FormTextArea.vue';
import ZelosLogo from '@/components/layouts/common-components/ZelosLogo.vue';
import DesktopHeader from '@/components/layouts/desktop/components/DesktopHeader.vue';
import NotesManager from '@/components/notes/NotesManager.vue';
import NotificationBar from '@/components/notification/NotificationBar.vue';
import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
import DataTableExpandableCell from '@/components/paginated-datatable/components/DataTableExpandableCell.vue';
import PaginatedDataTable from '@/components/paginated-datatable/PaginatedDataTable.vue';
import SectionWrapper from '@/components/section-wrapper/SectionWrapper.vue';
import CustomComponentWithDatePicker from '@/components/simple-inputs/CustomComponentWithDatePicker.vue';
import SimpleCheckbox from '@/components/simple-inputs/SimpleCheckbox.vue';
import SimpleDatePicker from '@/components/simple-inputs/SimpleDatePicker.vue';
import SimpleInputNumber from '@/components/simple-inputs/SimpleInputNumber.vue';
import SimpleInputText from '@/components/simple-inputs/SimpleInputText.vue';
import SimpleSelect from '@/components/simple-inputs/SimpleSelect.vue';
import SimpleTextArea from '@/components/simple-inputs/SimpleTextArea.vue';
import SlideWrapper from '@/components/slide-wrapper/SlideWrapper.vue';
import Toolbar from '@/components/toolbar/Toolbar.vue';
import TrackRecordingTable from '@/components/track-recording/TrackRecordingTable.vue';
import { useFocusManager } from '@/stores/focusManager';
import { useProfileStore } from '@/stores/profile';
import MultilingualPlugin from '@/plugins/multilingual';

import DetailModalOrPage from './components/details/detail-modal-or-page/DetailModalOrPage.vue';
import YousignStatusBadge from './components/layouts/common-components/YousignStatusBadge.vue';
import InternalLink from './components/navigation/InternalLink.vue';
import Notification from './components/notification/Notification.vue';
import ScrollableWrapper from './components/scrollable-wrapper/ScrollableWrapper.vue';
import SingleAccordion from './components/single-accordion/SingleAccordion.vue';
import { configureAxios } from './configs/axios.config';
import config, { loadConfig } from './plugins/global-config';
import i18n, { type SupportedLocale, supportedLocales } from './plugins/i18n';
import pinia from './plugins/pinia';
import { getPrimeVueConfigWithLocale, primevue } from './plugins/primevue';
import { useLanguageStore } from './stores/locale';
import { useTokenStore } from './stores/token';
import UiPluginInlineWrapper from './views/processes/components/UiPluginInlineWrapper.vue';
import UiPluginWrapper from './views/processes/components/UiPluginWrapper.vue';
import Contracts from './views/tickets/components/contracts/Contracts.vue';
import CostStatementInvoice from './views/tickets/components/cost-statement-invoice/CostStatementInvoice.vue';
import App from './App.vue';
import router from './router';

import './assets/main.css';
import 'primeicons/primeicons.css';
import '@mdi/font/css/materialdesignicons.css';

const app = createApp(App);

app.config.globalProperties.$config = config;

app.directive('styleclass', StyleClass);
app.directive('tooltip', Tooltip);

app.use(i18n);
app.use(MultilingualPlugin);
app.use(pinia);
app.use(router);
app.use(ConfirmationService);
app.use(ToastService);
app.use(DialogService);

app.component('Button', Button);
app.component('SelectButton', SelectButton);

app.component('Splitter', Splitter);
app.component('SplitterPanel', SplitterPanel);
app.component('Panel', Panel);

app.component('InputText', InputText);
app.component('InputNumber', InputNumber);
app.component('Textarea', Textarea);
app.component('Select', Select);
app.component('DatePicker', DatePicker);
app.component('Checkbox', Checkbox);
app.component('FloatLabel', FloatLabel);
app.component('Fieldset', Fieldset);
app.component('IconField', IconField);
app.component('InputIcon', InputIcon);
app.component('Paginator', Paginator);
app.component('Toolbar', Toolbar);
app.component('ButtonGroup', ButtonGroup);
app.component('ContextMenu', ContextMenu);
app.component('ConfirmDialog', ConfirmDialog);
app.component('Toast', Toast);
app.component('Tabs', Tabs);
app.component('TabList', TabList);
app.component('Tab', Tab);
app.component('TabPanels', TabPanels);
app.component('TabPanel', TabPanel);
app.component('PanelMenu', PanelMenu);
app.component('Badge', Badge);
app.component('Tag', Tag);
app.component('Dialog', Dialog);

app.component('ZelosLogo', ZelosLogo);
app.component('DataTable', DataTable);
app.component('PaginatedDataTable', PaginatedDataTable);
app.component('ExpandableCell', DataTableExpandableCell);
app.component('SectionWrapper', SectionWrapper);
app.component('FormInputText', FormInputText);
app.component('FormInputNumber', FormInputNumber);
app.component('FormTextArea', FormTextArea);
app.component('FormCheckbox', FormCheckbox);
app.component('FormSelect', FormSelect);
app.component('FormDatePicker', FormDatePicker);
app.component('FormLazySelect', FormLazySelect);

app.component('SimpleInputText', SimpleInputText);
app.component('SimpleInputNumber', SimpleInputNumber);
app.component('SimpleDatePicker', SimpleDatePicker);
app.component('SimpleSelect', SimpleSelect);
app.component('SimpleCheckbox', SimpleCheckbox);
app.component('SimpleTextArea', SimpleTextArea);
app.component('DetailToolbar', DetailToolbar);
app.component('DetailFormfooter', DetailFormfooter);
app.component('DetailRelations', DetailRelations);
app.component('FileUpload', FileUpload);
app.component('ToggleButton', ToggleButton);
app.component('ToggleSwitch', ToggleSwitch);
app.component('Card', Card);
app.component('Accordion', Accordion);
app.component('AccordionPanel', AccordionPanel);
app.component('AccordionHeader', AccordionHeader);
app.component('AccordionContent', AccordionContent);
app.component('DesktopHeader', DesktopHeader);
app.component('DynamicDialog', DynamicDialog);
app.component('FocusArea', FocusArea);
app.component('TrackRecordingTable', TrackRecordingTable);
app.component('FileViewer', FileViewer);
app.component('Notification', Notification);
app.component('NotificationBar', NotificationBar);
app.component('ScrollableWrapper', ScrollableWrapper);
app.component('ProgressSpinner', ProgressSpinner);
app.component('PageWrapper', PageWrapper);
app.component('SlideWrapper', SlideWrapper);
app.component('Divider', Divider);
app.component('CommentBubble', CommentBubble);
app.component('NotesManager', NotesManager);
app.component('ContentTabs', ContentTabs);
app.component('ContentTab', ContentTab);
app.component('CustomComponentWithDatePicker', CustomComponentWithDatePicker);
app.component('SingleAccordion', SingleAccordion);
app.component('YousignStatusBadge', YousignStatusBadge);
app.component('UiPluginWrapper', UiPluginWrapper);
app.component('UiPluginInlineWrapper', UiPluginInlineWrapper);
app.component('CostStatementInvoice', CostStatementInvoice);
app.component('Contracts', Contracts);
app.component('ModalOrPage', DetailModalOrPage);
app.component('InternalLink', InternalLink);

app.directive('ripple', Ripple);

// Globale Tastatur-Events
document.addEventListener('keydown', (event) => {
  const focusManager = useFocusManager();
  if (event.key === 'ArrowRight') {
    focusManager.focusNextArea(event);
  } else if (event.key === 'ArrowLeft') {
    focusManager.focusPreviousArea(event);
  }
});

loadConfig()
  .then(async () => {
    configureAxios();

    // Fetch user profile after axios is configured if token exists
    const tokenStore = useTokenStore();
    const profileStore = useProfileStore();
    if (tokenStore.accessToken && !profileStore.userProfile) {
      void profileStore.fetchUserProfile();
    }

    const languageStore = useLanguageStore();
    const lang: SupportedLocale = languageStore.locale as SupportedLocale;

    if (supportedLocales.includes(lang)) {
      i18n.global.locale.value = lang;
      app.use(primevue, getPrimeVueConfigWithLocale(lang));
    }

    await router.isReady();
  })
  .catch((error) => {
    console.error('Failed to load configuration', error);
  })
  .finally(() => {
    app.mount('#app');
  });
